import { callAIJson } from '@4cm/core/ai';
import { supabase, isPlaceholderSupabase } from './supabase';
import { appendLocalDevPlan } from './demoData';
import type { RadarValues } from './queries/useTeamRadar';

/**
 * Brain-led development plan composer.
 *
 * Coach gives a focus theme. Brain returns a 4-week plan: a primary
 * corner, 3 milestones with due dates, and a final review date.
 * Saves to development_plans table.
 */

export interface PlanMilestone {
  text: string;
  due_date: string;
  met_at?: string | null;
}

export interface DraftedPlan {
  primary_corner: 'tech' | 'phys' | 'psych' | 'social';
  rationale: string;
  milestones: PlanMilestone[];
  review_date: string;
}

interface DraftOptions {
  playerName: string;
  position?: string | null;
  focusTheme: string;
  ratings?: RadarValues;
  ratingsCount?: number;
  goalFacts?: string[];
}

const SYSTEM_PROMPT = `You compose 4-week player development plans for a youth football coach.

Return ONLY valid JSON. No prose, no markdown fences. Schema:
{
  "primary_corner": "tech" | "phys" | "psych" | "social",
  "rationale": string,
  "milestones": [
    { "text": string, "due_date": "YYYY-MM-DD" },
    { "text": string, "due_date": "YYYY-MM-DD" },
    { "text": string, "due_date": "YYYY-MM-DD" }
  ],
  "review_date": "YYYY-MM-DD"
}

Rules:
- Plan length is 4 weeks from today.
- Three milestones evenly spaced across the four weeks.
- Each milestone is ONE concrete observable behaviour ("hits 3 of 5 first-touch reps under press in Tue session"), not a generic outcome.
- review_date is end of week 4.
- primary_corner picks the corner the focus theme most clearly addresses.
- rationale is 1 sentence, plain, addressed to the coach.`;

export async function draftDevelopmentPlan(opts: DraftOptions): Promise<DraftedPlan | null> {
  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);
  const userContent = JSON.stringify({
    today: todayIso,
    player_name: opts.playerName,
    position: opts.position ?? null,
    focus_theme: opts.focusTheme,
    ratings_28d: opts.ratings ?? null,
    ratings_count: opts.ratingsCount ?? 0,
    active_goals: opts.goalFacts ?? [],
  });

  const payload = await callAIJson<DraftedPlan>({
    system: SYSTEM_PROMPT,
    userContent,
    maxTokens: 600,
    surface: 'brain-chat',
    effort: 'low',
  });
  if (!payload || typeof payload !== 'object') return null;
  if (!Array.isArray(payload.milestones) || payload.milestones.length < 1) return null;
  return payload;
}

interface SaveOptions {
  coachId: string;
  playerId: string;
  plan: DraftedPlan;
}

export async function saveDevelopmentPlan(
  opts: SaveOptions,
): Promise<{ ok: true; id: string } | { ok: false; error: string }> {
  const { coachId, playerId, plan } = opts;
  const today = new Date();
  const periodStart = today.toISOString().slice(0, 10);
  const periodEnd = new Date(today.getTime() + 28 * 86_400_000).toISOString().slice(0, 10);

  // Offline path — writes to localStorage so the plan survives reload.
  if (isPlaceholderSupabase()) {
    try {
      const row = appendLocalDevPlan({
        coach_id: coachId,
        player_id: playerId,
        period_start: periodStart,
        period_end: periodEnd,
        primary_corner: plan.primary_corner,
        milestones: plan.milestones,
        review_date: plan.review_date,
      });
      return { ok: true, id: row.id };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Could not save plan locally.' };
    }
  }

  const { data, error } = await supabase
    .from('development_plans')
    .insert({
      player_id: playerId,
      coach_id: coachId,
      period_start: periodStart,
      period_end: periodEnd,
      corner_priorities: { [plan.primary_corner]: 1 },
      milestones: plan.milestones,
      review_date: plan.review_date,
    })
    .select('id')
    .single();

  if (error || !data) return { ok: false, error: error?.message ?? 'Could not save plan.' };
  return { ok: true, id: String(data.id) };
}
